import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { OutcomePage } from '@/components/layout/OutcomePage.js';
import { Button } from '@/components/ui/button.js';
import { useCatalog } from '@/lib/i18n/catalog-context.js';

import { ROUTES } from './routes.js';

/**
 * Экран «страница не найдена». Источник — ../MathMindPHP/views/error.php.
 *
 * ⚠️ Два расхождения с продом, оба осознанные (решение владельца):
 *
 * 1. Текст АРМЯНСКИЙ. В проде это был единственный англоязычный экран во всём продукте
 *    («Oops! You're lost.») при полностью армянском интерфейсе.
 * 2. Код ответа остаётся 200. Страницу рисует SPA поверх ответа spaFallback, и поменять
 *    статус из браузера нельзя. В PHP было так же: неизвестный путь редиректил на /error,
 *    который тоже отдавал 200.
 *
 * ⚠️ Значка исхода здесь НЕТ — его место занимает само «404» цветом `--mm-surface`.
 * Число и есть значок: оно крупнее любого круга и не требует объяснения.
 *
 * ⚠️ Число помечено `data-decorative` — это единственное такое место в продукте, и метка
 * не косметическая: она выводит узел из проверки контраста (`tests/e2e/a11y-contrast`).
 * Основание — WCAG 1.4.3, где у чистого декора требования контраста нет: «404» скрыто от
 * скринридера, а его смысл полностью несут заголовок и абзац под ним, так что удаление
 * числа не отнимает ни одного факта. Затемнять его до 3:1 значило бы превратить водяной
 * знак в третий по силе элемент экрана, спорящий с заголовком.
 *
 * Метка ставится ТОЛЬКО так: текст скрыт от скринридера И продублирован рядом видимой
 * надписью. Не выполнено одно из двух — цвет чинится, а не помечается.
 */
export function NotFoundPage(): ReactElement {
  const c = useCatalog();

  return (
    <OutcomePage>
      <p
        aria-hidden="true"
        data-decorative="true"
        className="text-[96px] leading-none font-extrabold tracking-[-0.05em] text-mm-surface"
      >
        {c.errors.notFound.code}
      </p>
      <h1 className="text-h3">{c.errors.notFound.title}</h1>
      <p className="max-w-[460px] text-[17px] leading-relaxed text-mm-text-muted text-pretty">
        {c.errors.notFound.description}
      </p>
      <Button asChild className="mt-1 h-12 px-7 text-base">
        <Link to={ROUTES.home}>{c.common.backToHome}</Link>
      </Button>
    </OutcomePage>
  );
}
